import React from "react";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import { getAILinkClipsWithQuerry } from "./ClipsUtilities/getAILinkClipsWithQuerry";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { updateClipsSearchParams } from "./ClipsUtilities/clipsUrlParams";
import { useClipsStore } from "./ClipsStore/clipsStore";

export const ApplyFiltersButton = ({
  setClipsData,
  setPageLoading,
  setSelectedRow,
  setPrecisionRecallData,
  setVideoURL,
  setVideoClipLoading,
  setClipsIndexOrder,
  setVerifiedClicked,
  setUnverifiedClicked,
  selectedRow,
  setNoDataToDisplay,
  setSiteOptions,
  setLoadingSites,
  userFilters,
  setFetchingFilteredData,
  setPage,
  setAiLinkClipsTotal,
  setLoadingAdditionalData,
  setFiltersApplied,
  navigate,
  setSearchParams,
  groupID,
}: {
  setClipsData: any;
  setPageLoading: Dispatcher<boolean>;
  setSelectedRow: any;
  setPrecisionRecallData: any;
  setVideoURL: any;
  setVideoClipLoading: Dispatcher<boolean>;
  setClipsIndexOrder: any;
  setVerifiedClicked: Dispatcher<boolean>;
  setUnverifiedClicked: Dispatcher<boolean>;
  selectedRow: any;
  setNoDataToDisplay: Dispatcher<boolean>;
  setSiteOptions: any;
  setLoadingSites: any;
  userFilters: any;
  setFetchingFilteredData: Dispatcher<boolean>;
  setPage: any;
  setAiLinkClipsTotal: any;
  setLoadingAdditionalData: Dispatcher<boolean>;
  setFiltersApplied: Dispatcher<boolean>;
  navigate: NavigateFunction;
  setSearchParams?: (params: URLSearchParams) => void;
  groupID?: number | null;
}) => {
  const options = ["Apply Filters", "Download Report"];

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [option, setOption] = React.useState(options[0]);

  const { downloadURLAILink, fetchingDownloadURLAILink, getDownloadURLAILink } =
    useClipsStore();

  const handleRefreshReport = () => {
    getDownloadURLAILink(navigate, userFilters, true, groupID);
  };

  const handleClick = () => {
    if (option === "Apply Filters") {
      setFiltersApplied(true);
      setLoadingAdditionalData(true);
      setAiLinkClipsTotal(0);
      setSelectedRow({});
      setNoDataToDisplay(true);
      setFetchingFilteredData(true);
      setClipsData([]);
      setPage(0);
      setPrecisionRecallData({
        date: "",
        date_str: "",
        true_positives: 0,
        false_positives: 0,
        false_negatives: 0,
        true_negatives: 0,
        unclassified: 0,
        total_clips: 0,
        precision: null,
        recall: null,
      });
      localStorage.setItem("last_file_id_ailink", "");
      localStorage.setItem("last_timestamp_ailink", "");
      getAILinkClipsWithQuerry(
        setClipsData,
        setPageLoading,
        setSelectedRow,
        setPrecisionRecallData,
        setVideoURL,
        setVideoClipLoading,
        setClipsIndexOrder,
        setVerifiedClicked,
        setUnverifiedClicked,
        selectedRow,
        setNoDataToDisplay,
        setSiteOptions,
        setLoadingSites,
        userFilters,
        setFetchingFilteredData,
        setPage,
        false,
        "",
        "",
        setLoadingAdditionalData,
        navigate
      );
      if (setSearchParams) {
        updateClipsSearchParams(setSearchParams, "ailink", userFilters, {
          groupId: groupID ?? undefined,
        });
      }
    }
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    setOption(options[index]);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  return (
    <div className="z-10">
      <React.Fragment>
        <ButtonGroup
          variant="contained"
          ref={anchorRef}
          aria-label="Site Group Actions"
          disabled={false}
          color="warning"
        >
          {option === "Apply Filters" ? (
            <Button
              onClick={handleClick}
              disabled={false}
              sx={{
                backgroundColor: "#FF8400",
                "&:hover": { backgroundColor: "#E67300" },
                width: "140px",
              }}
            >
              {option}
            </Button>
          ) : (
            <Button
              onClick={() => {}}
              component="a"
              href={downloadURLAILink}
              download
              disabled={!downloadURLAILink}
              sx={{
                color: "white",
                backgroundColor: "#FF8400",
                "&:hover": {
                  backgroundColor: "#E67300",
                  color: "white",
                },
                width: "178px",
                textDecoration: "none",
              }}
            >
              {fetchingDownloadURLAILink
                ? "Fetching Report..."
                : "Download Report"}
            </Button>
          )}
          {option === "Download Report" && (
            <Tooltip title="Regenerate the report with the latest clips">
              <Button
                size="small"
                aria-label="refresh report"
                disabled={fetchingDownloadURLAILink}
                sx={{
                  backgroundColor: "#FF8400",
                  "&:hover": { backgroundColor: "#E67300" },
                }}
                onClick={handleRefreshReport}
              >
                <RefreshIcon />
              </Button>
            </Tooltip>
          )}
          <Button
            size="small"
            aria-controls={open ? "split-button-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-label="select group action"
            aria-haspopup="menu"
            sx={{
              backgroundColor: "#FF8400",
              "&:hover": { backgroundColor: "#E67300" },
            }}
            onClick={handleToggle}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Popper
          sx={{
            zIndex: 1,
          }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    className="absolute"
                    id="split-button-menu"
                    autoFocusItem
                  >
                    {options.map((option, index) => (
                      <MenuItem
                        key={option}
                        selected={index === options.indexOf(option)}
                        onClick={(event) => handleMenuItemClick(event, index)}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </React.Fragment>
    </div>
  );
};
